import { initializeApp, getApps } from 'firebase/app'
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore'

// ---------------------------------------------------------------------------
// Firebase initialisation
// ---------------------------------------------------------------------------
// Values are loaded from .env (REACT_APP_ prefix required by Create React App).
// Copy .env.example → .env and fill in your project's values.

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

// Avoid re-initialising on hot reload
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0]
export const firestore = getFirestore(app)

// ---------------------------------------------------------------------------
// Clashdle game config — stored in Firestore at clashdle/game
// ---------------------------------------------------------------------------

export interface WordSchedule {
  currentWord: string
  currentWordDate: string
  nextWord: string | null
  nextWordScheduledFor: string | null
  isLocked?: boolean
}

/**
 * Reads the current game config from Firestore.
 * Falls back to localStorage cache, then to a safe default if Firestore fails.
 */
export const fetchGameConfig = async (): Promise<WordSchedule> => {
  try {
    const docRef = doc(firestore, 'clashdle', 'game')
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      const config = docSnap.data() as WordSchedule
      // Cache locally for offline fallback
      localStorage.setItem('clashdle_currentWord', JSON.stringify(config))
      return config
    }

    // First-time default — admin should update via the admin panel
    const defaultConfig: WordSchedule = {
      currentWord: 'CHAMP',
      currentWordDate: new Date().toISOString().split('T')[0],
      nextWord: null,
      nextWordScheduledFor: null,
    }
    return defaultConfig
  } catch (error) {
    console.error('Firestore fetch error:', error)
    // Try localStorage cache as fallback
    const cached = localStorage.getItem('clashdle_currentWord')
    if (cached) {
      console.log('Using cached word from localStorage')
      return JSON.parse(cached) as WordSchedule
    }
    // Ultimate fallback
    return {
      currentWord: 'CHAMP',
      currentWordDate: new Date().toISOString().split('T')[0],
      nextWord: null,
      nextWordScheduledFor: null,
    }
  }
}

/**
 * Writes an updated game config to Firestore.
 * Also caches to localStorage for offline fallback.
 * Only called from the Admin panel.
 */
export const updateGameConfig = async (config: WordSchedule): Promise<void> => {
  try {
    const docRef = doc(firestore, 'clashdle', 'game')
    await setDoc(docRef, config)
    // Cache locally on successful write
    localStorage.setItem('clashdle_currentWord', JSON.stringify(config))
  } catch (error) {
    console.error('Firestore update error:', error)
    throw new Error('Failed to update game configuration. Please check your connection.')
  }
}

// ---------------------------------------------------------------------------
// Admin password — stored in Firestore at admin_settings/auth
// (shared with the main website; read-only from the frontend)
// ---------------------------------------------------------------------------

/**
 * Fetches the admin password from Firestore.
 * Returns null if the document doesn't exist or the fetch fails.
 */
export const fetchAdminPassword = async (): Promise<string | null> => {
  try {
    const docRef = doc(firestore, 'admin_settings', 'auth')
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      return data.password || null
    }
    return null
  } catch (e) {
    console.error('Failed to fetch admin password from Firestore:', e)
    return null
  }
}
